import type { components } from "@/api-schema";
import { useModelFamilySearchQuery } from "@/common/api/model-family/use-model-family-search.query";
import { useComboboxPagination } from "@/common/hooks/use-combobox-pagination";
import type { BaseFilterProps, FilterData, FilterPaginationState } from "../_types";
import { Filter } from "../_types";
import { FilterComponent } from "../base-filter";
import type { JSX } from "react";

export type QueryBody = components["schemas"]["ModelFamilySearchBody"];

const FILTER_PAGE_SIZE = 50;

function useModelFamilyFilterData(body: QueryBody): { data: FilterData; pagination: FilterPaginationState } {
  const { debouncedSearchTerm, pageSize, createPaginationState } = useComboboxPagination({
    initialPageSize: FILTER_PAGE_SIZE,
  });

  const {
    data: modelFamilies,
    isPending: isLoading,
    error,
  } = useModelFamilySearchQuery({
    options: {
      parameters: {
        pageSize,
        search: debouncedSearchTerm || undefined,
      },
      body,
    },
  });

  const options =
    modelFamilies?.data
      .map((element) => ({
        id: element.uuid ?? "",
        name: element.name ?? "",
      }))
      .filter((option) => option.id && option.name)
      .filter((option, index, array) => array.findIndex((item) => item.id === option.id) === index) || [];

  const loadedCount = modelFamilies?.data?.length ?? 0;
  const total = modelFamilies?.meta?.total ?? 0;
  const pagination = createPaginationState(total, loadedCount, isLoading);

  return {
    data: {
      options,
      isLoading,
      error,
    },
    pagination,
  };
}

export class ModelFamilyFilter extends Filter<QueryBody, unknown> {
  readonly id: string = "modelFamilies";
  readonly labelSingular = "common:ModelFamily";
  readonly labelPlural = "common:ModelFamilies";
  readonly placeholder = "common:SelectModelFamily";
  readonly searchPlaceholder = "common:SearchModelFamilies";
  readonly emptyText = "common:NoModelFamiliesFound";

  constructor({ id, body = {} }: { id?: string; body?: Partial<QueryBody> } = {}) {
    super();
    if (id) this.id = id;
    this.body = body as QueryBody;
  }

  getData(): FilterData {
    return { options: [], isLoading: false, error: null };
  }

  render(props: BaseFilterProps): JSX.Element {
    return <ModelFamilyFilterComponent filter={this} {...props} />;
  }
}

function ModelFamilyFilterComponent({
  filter,
  ...props
}: { filter: ModelFamilyFilter } & BaseFilterProps): JSX.Element {
  const { data, pagination } = useModelFamilyFilterData(filter.body);

  const filterWithData = Object.create(filter);
  filterWithData.getData = () => data;

  return <FilterComponent filter={filterWithData} pagination={pagination} {...props} />;
}
